import React, { Fragment } from "react";
import * as PropTypes from "prop-types";
import cx from "classnames";
import {
	Grid,
	FormControl,
	InputLabel,
	FormHelperText,
	Tooltip,
	TextField,
	Box, ClickAwayListener, useMediaQuery
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import OutlinedInput from "@mui/material/OutlinedInput";
import InfoIcon from "@mui/icons-material/Info";
import * as _ from "lodash";
import InputAdornment from "@mui/material/InputAdornment";
import { useTranslation } from "react-i18next";
import theme from "../../../theme";
import colors from "../../../../utils/colors";

const useStyles = makeStyles({
	cssFocused: {
		"& svg": {
			color: colors.octaBlue
		}
		// transform: "translate(24px, -6px) scale(1)"
	},
	inputAction: {
		width: "50%"
	},
	cssOutlinedInput: {
		borderRadius: "40px",
		borderColor: colors.white,
		backgroundColor: colors.inputBackground,
		fontWeight: 500,
		fontSize: "14px",
		color: colors.octaBlue,
		"&.Mui-focused": {
			border: `1px solid ${colors.borderLight}`,
			"&:hover": {
				borderColor: colors.octaBlue
			}
		},
		"&:hover .MuiOutlinedInput-notchedOutline": {
			borderColor: colors.octaBlue
		},
		"& input": {
			padding: "17px 22px 15px"
		},
		"& svg": {
			borderRadius: "50px",
			// background: colors.gray,
			color: colors.black,
			fontSize: 40,
			padding: theme.spacing(1)
		}
	},
	cssLabel: {
		padding: "5px 0",
		color: "rgb(31 32 34)",
		fontWeight: "bold",
		fontSize: "14px",
		lineHeight: 1.25,
		position: "relative",
		transform: "none",
		"&.Mui-focused": {
			color: colors.octaBlue
		}
	},
	labelOutlined: {
		transform: "translate(25px, 10px) scale(1)"
	},
	formHelperRoot: {
		fontSize: "14px",
		textTransform: "none",
		marginTop: "4px",
		fontWeight: "normal"
	},
	inputSpacing: {
		marginTop: "4px"
	},
	cssOutline: {
		borderRadius: "40px",
		borderColor: colors.white,
		"&:hover": {
			borderColor: `${colors.octaBlue}`
		}
	},
	tooltipStyles: {
		top: "4px !important"
	},
	tooltipLabel: {
		fontSize: "16px"
	},
	tooltipRoot: {
		"& svg": {
			margin: "0 5px",
			width: "16px",
			height: "16px",
			fill: colors.octaBlue,
			cursor: "pointer"
		}
	},
	disabledStyles: {
		"&.Mui-focused": {
			borderColor: "rgba(0, 0, 0, 0.26)",
			"&:hover": {
				borderColor: `rgba(0, 0, 0, 0.26) !important`
			}
		},
		"&:hover .MuiOutlinedInput-notchedOutline": {
				borderColor: `rgba(0, 0, 0, 0.26) !important`
		}
	},
	disabledOutlineStyles: {
		"&:hover": {
			borderColor: `rgba(0, 0, 0, 0.26) !important`
		}
	}
});

export const Input = (props: any) => {
	const classes = useStyles();
	const { t } = useTranslation("common");
	const {
		id,
		name,
		type,
		label,
		placeholder,
		helperText,
		value,
		onChange,
		error,
		icon,
		disabled,
		readOnly,
		required,
		hasAction,
		InputLabelProps,
		inputProps,
		InputProps,
		tooltipText,
		tooltipIcon,
		unit,
		onKeyPress,
		autoFocus,
		onBlur,
		...rest
	} = props;
	const [open, setOpen] = React.useState(false);

	const handleTooltipClose = () => {
		setOpen(false);
	};

	const handleTooltipOpen = (event: any) => {
		setOpen(true);
		event.stopPropagation();
		event.preventDefault();
	};

	const mdDown = useMediaQuery((theme: any) => theme.breakpoints.down("md"));

	return (
        <FormControl
			variant="standard"
			classes={{
				root: cx(classes.inputSpacing)
			}}
			error={error}
			disabled={disabled}
			required={required}
			fullWidth
		>
			{label && (
				<Grid
					container
					item
					direction="row"
					alignItems="center"
					className={classes.tooltipRoot}
				>
					<InputLabel
						htmlFor={name}
						classes={{
							root: classes.cssLabel,
							focused: classes.cssFocused,
							outlined: classes.labelOutlined
						}}
						shrink={false}
					>
						{label}
					</InputLabel>
					{tooltipText !== "" && (
						<>
							{(mdDown) ? (
								<ClickAwayListener onClickAway={handleTooltipClose}>
									<div>
										<Tooltip
											placement="top"
											PopperProps={{
												disablePortal: true
											}}
											onClose={handleTooltipClose}
											open={open}
											title={tooltipText}
											classes={{
												popper: classes.tooltipStyles,
												tooltip: classes.tooltipLabel
											}}
											disableFocusListener
											disableHoverListener
											disableTouchListener
											arrow
										>
											<InfoIcon onClick={handleTooltipOpen} />
										</Tooltip>
									</div>
								</ClickAwayListener>
							) : (
								<Tooltip
									placement="top"
									title={tooltipText}
									classes={{
										popper: classes.tooltipStyles,
										tooltip: classes.tooltipLabel
									}}
									disableFocusListener
									arrow
								>
									<InfoIcon />
								</Tooltip>
							)}
						</>
					)}
				</Grid>
			)}
			<OutlinedInput
				id={id}
				name={name}
				type={type}
				value={value}
				placeholder={placeholder}
				onChange={onChange}
				onKeyPress={onKeyPress}
				error={error}
				disabled={disabled}
				readOnly={readOnly}
				autoFocus={autoFocus}
				required={required}
				onBlur={onBlur}
				classes={{
					root: cx(classes.cssOutlinedInput, {[classes.disabledStyles]: disabled}),
					notchedOutline: cx(classes.cssOutline, {[classes.disabledStyles]: disabled}),
					inputAdornedEnd: hasAction ? classes.inputAction : ""
				}}
				endAdornment={
					icon ||
					(_.isEmpty(unit) ? null : (
						<Box>
							<InputAdornment position="end">{t(`${unit}`)}</InputAdornment>
						</Box>
					))
				}
				fullWidth
				inputProps={{
					...inputProps
				}}
				{...InputProps}
				{...InputLabelProps}
				{...rest}
			/>
			{helperText && (
				<FormHelperText className={classes.formHelperRoot}>
					{helperText}
				</FormHelperText>
			)}
		</FormControl>
    );
};

Input.defaultProps = {
	type: "text",
	error: false,
	readOnly: false,
	label: "",
	placeholder: "",
	helperText: "",
	disabled: false,
	required: false,
	withTooltip: false,
	tooltipText: "",
	tooltipIcon: <InfoIcon />,
	hasAction: false,
	autoFocus: false,
	unit: ""
};

Input.propTypes = {
	error: PropTypes.bool,
	type: PropTypes.string,
	withTooltip: PropTypes.bool,
	tooltipText: PropTypes.string,
	placeholder: PropTypes.string,
	helperText: PropTypes.string,
	readOnly: PropTypes.bool,
	disabled: PropTypes.bool,
	required: PropTypes.bool,
	hasAction: PropTypes.bool,
	autoFocus: PropTypes.bool,
	icon: PropTypes.element,
	tooltipIcon: PropTypes.any,
	inputProps: PropTypes.shape({}),
	InputProps: PropTypes.shape({}),
	InputLabelProps: PropTypes.shape({}),
	id: PropTypes.string.isRequired,
	name: PropTypes.string.isRequired,
	label: PropTypes.string,
	value: PropTypes.string,
	onChange: PropTypes.func,
	onKeyPress: PropTypes.func,
	onBlur: PropTypes.func,
	unit: PropTypes.string
};

export default React.memo(Input);
